import React from 'react';
import { Grid, Paper, Typography, Box } from '@mui/material';

const PersonalDetailsGrid = ({ colleague }) => {
  const allItems = [
    { label: 'Fun Fact', value: colleague?.fun_fact, icon: '🎉', color: '#FFF0F5' },
    { label: 'Hidden Skill', value: colleague?.hidden_skill, icon: '✨', color: '#F0F8FF' },
    { label: 'Hobbies & Interests', value: colleague?.hobbies_interests, icon: '🎯', color: '#F0FFF0' },
    { label: 'Dream Destination', value: colleague?.travel_destination, icon: '✈️', color: '#FFF8DC' },
    { label: 'Daily Mantra', value: colleague?.daily_mantra, icon: '💭', color: '#F5F0FF' },
    { label: 'Superpower', value: colleague?.superpower_choice, icon: '⚡', color: '#FFF5EE' },
    { label: 'Dream Dinner Guest', value: colleague?.dinner_guest, icon: '🍽️', color: '#F0FFFF' },
    { label: 'Bucket List', value: colleague?.bucket_list_item, icon: '📝', color: '#FFFFED' },
    { label: '5-Year Goals', value: colleague?.five_year_goals, icon: '🎯', color: '#F0F5FF' },
    { label: 'Additional Info', value: colleague?.extra_info, icon: 'ℹ️', color: '#F8F8FF' }
  ].filter(item => item.value);

  // Get border color for each background color
  const getBorderColor = (color) => {
    const colorMap = {
      '#F0F8FF': '#B0E0E6',
      '#F0FFF0': '#98FB98',
      '#FFF8DC': '#F0E68C',
      '#F5F0FF': '#DDA0DD',
      '#FFF5EE': '#FFB6C1',
      '#F0FFFF': '#AFEEEE',
      '#FFFFED': '#F0E68C',
      '#F0F5FF': '#B0C4DE',
      '#F8F8FF': '#D3D3D3',
      '#FFF0F5': '#FFB6C1'
    };
    return colorMap[color] || '#FFB6C1';
  };

  // Categorize items by content length for intelligent layout
  const shortItems = allItems.filter(item => item.value.length <= 50);
  const mediumItems = allItems.filter(item => item.value.length > 50 && item.value.length <= 100);
  const longItems = allItems.filter(item => item.value.length > 100);

  // Create intelligent layout with 4 boxes per row for short items
  const layoutRows = [];
  let currentRow = [];
  let currentRowWidth = 0;

  // Process short items first (4 per row)
  shortItems.forEach(item => {
    if (currentRow.length < 4) {
      currentRow.push({ ...item, width: 3 }); // 3 columns for 4 items per row
      currentRowWidth += 3;
    } else {
      layoutRows.push([...currentRow]);
      currentRow = [{ ...item, width: 3 }];
      currentRowWidth = 3;
    }
  });

  // Process medium items (2 per row)
  mediumItems.forEach(item => {
    if (currentRow.length === 0) {
      currentRow.push({ ...item, width: 6 }); // 6 columns for 2 items per row
      currentRowWidth = 6;
    } else if (currentRow.length === 1 && currentRowWidth === 3) {
      // If we have 1 short item, add medium item to complete row
      currentRow.push({ ...item, width: 9 });
      currentRowWidth = 12;
    } else if (currentRow.length < 2 && currentRowWidth === 6) {
      currentRow.push({ ...item, width: 6 });
      currentRowWidth = 12;
    } else {
      layoutRows.push([...currentRow]);
      currentRow = [{ ...item, width: 6 }];
      currentRowWidth = 6;
    }
  });

  // Process long items (always full width)
  longItems.forEach(item => {
    if (currentRow.length > 0) {
      layoutRows.push([...currentRow]);
      currentRow = [];
      currentRowWidth = 0;
    }
    layoutRows.push([{ ...item, width: 12 }]);
  });

  // Add remaining items
  if (currentRow.length > 0) {
    layoutRows.push([...currentRow]);
  }

  return (
    <Box>
      {layoutRows.map((row, rowIndex) => (
        <Grid container spacing={2} key={rowIndex} sx={{ mb: 2 }}>
          {row.map((item, itemIndex) => (
            <Grid item xs={12} sm={item.width} key={`${rowIndex}-${itemIndex}`}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  bgcolor: item.color, 
                  borderRadius: 2,
                  height: '100%',
                  border: `1px solid ${getBorderColor(item.color)}`
                }}
              >
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#0D004A', 
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: '0.9rem'
                  }}
                >
                  <span>{item.icon}</span> {item.label}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.85rem',
                    lineHeight: 1.4
                  }}
                >
                  {item.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
};

export default PersonalDetailsGrid;
